import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute, { BootLoading } from './components/common/ProtectedRoute';
import DefaultRouteRedirect from './components/common/DefaultRouteRedirect';
// V17.325: one list for every account-book gate (V17.523: the route here and the menu registry
// src/app/navigation.ts), so they cannot drift apart again.
import { ACCOUNT_READ_PERMISSIONS } from './constants/accountPermissions';
import Login from './pages/Login';

// V5.0 Commercial Workflow Pages (Part 4)

// V7.0 Sales Performance Dashboard
// V17.70: Sales Manager Dashboard

// V11.0 Portfolio Management (Relationship Manager)

// V16.1 3-Level Approval Workflow
// V17.16.60 Discount Approval Workflow
// V17.60 Full-page Approval Review

// V16.4 Commercial Scout Territory Mapping & Lead Generation
// V16.13 Scout Leader GPS Audit Tool
// V16.13.4 Scout "My Tasks" Page
// V16.14 Lead Review Dashboard for Team Leaders
// V16.15 Commercial Manager Inbox
// V16.15 Assigned Leads Page (Sales Rep workspace)
// V17.19.0 Contract List Page (AR Officer / Sales Manager)
// V17.59.4 Contract Generation Page (full-page, replaces modal)
// V17.64 Contract Update Page
// V17.63 Upsell Queue
// V17.73 Upsell Surplus Report Flow

// V17.342 Cross-Border Reports

// V13.0 Field Force & Logistics
// V17.16.42 Completed Customers Report
// V17.16 Transportation Commercial Dashboard with SLA
// V17.58.4 TCO Site Check & Journey Plan
// V17.80 Site Check Center (Site Check Officer allocation)
// V17.59.6 Completed Customer Detail Page

// V17.253 Transport Team Workspace

// V17.61 Fleet Management & PJP Routes
// V17.368: IH planning app phase 2a — daily plan calendar + plan detail (publish)

// V15.0 Stage 0 Scouting Infrastructure
// V15.1 Scout Command Center

// V5.0 Residential Workflow Pages (Part 5)
// V9.1 Residential Package & Amendment Engine
// V15.3 Residential Sales Rep Dashboard
// V16.0 Team Leader Command Center
// V16.1 TSO Verification Dashboard
// V16.1 Dual-Approval Account Closing

// Advanced Reporting Module Pages

// V17.64 Admin Dashboard Hub

// V6.0 Admin Pages (CRITICAL P1 UAT FIX)

// V7.0 Admin Pages (Dynamic Taxonomy Management + Role Management)
// V16.15 Permissions Management (User Management Group)

// V8.0 Admin Pages (Unified Audit Log)

// V16.9 Admin Pages (Location Data Governance)

// V16.15 Commercial Teams Management (Admin)

// V17.0 Departments Management (Admin)

// V17.16.60 Discount Approval Settings (Admin)

// V8.2 Pricing Management Pages (Segregation of Duties)
// V8.2 SoD: Service Catalog moved to Admin workspace (Admin permission required)
// V8.2 SoD: Price Book remains in Finance workspace (Finance Manager permission)
// V9.1 Residential Package Management (Finance Manager)
// V9.0 Exception Queue (Finance Manager + Line Manager)
// Financial Control Tower - Cashier Dashboard (Finance Manager)
// V17.0 AR Dashboard (AR Officer + Debt Collector)
// V17.22.0 AR Team Restructuring - Commercial Leader & Officer Dashboards
// V4.0 AR Module Phase 2-3
// V17.28 AR Account Management KPIs
// V17.28 AR Team Performance Dashboard
// V17.23.0 AR Engine - New Leader Dashboard and Officer Workspace
// V17.128 AR Residential - Officer Workspace
// V17.505 AR Field Officer map (row 72 brief (a)). Lazy, so its keyless map
// code ships in its own chunk.
import { lazy, Suspense } from 'react';
const ARFieldOfficerMap = lazy(() => import('./pages/ar/field/ARFieldOfficerMap'));
// V17.516 (Phase 0): real 403 / 404 pages.
import Forbidden from './pages/Forbidden';
import { useIsDesktop } from './components/layout/Layout';
import NotFound from './pages/NotFound';
// V17.506 (row 72 brief (b)): desktop leader + CS queues.
const ARFieldLeaderQueue = lazy(() => import('./pages/ar/field/LeaderQueuePage'));
const ARFieldCsQueue = lazy(() => import('./pages/ar/field/CsQueuePage'));
// V17.212 AR Residential - Payment Report
// V17.247 AR Residential - Daily Calls
// V17.300 AR Residential - Call Plan
// V17.261 AR Residential - Officer detail (leader drill-down)
// V17.128 PR 3: AR Residential leader UI
// V17.262 AR Residential - Daily Team Board (page + ?tv=1 wall display)
import { AppRootGate } from './pages/ar/appRootGate';
// Brief 2026-10-30-006: a stale page chunk after a deploy reloads once, then shows a message.
import { ChunkErrorBoundary } from './components/common/ChunkErrorBoundary';

// V17.523 (Phase 1, brief 2026-10-26-002, F09): every page is loaded on demand, so the main
// chunk carries the shell only. Suspense fallbacks: App (boot) and Layout (page area).
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SelectRole = lazy(() => import('./pages/SelectRole'));  // V17.71 Multi-Role Selector
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Sync = lazy(() => import('./pages/Sync'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Users = lazy(() => import('./pages/Users'));
const CustomerAccounts = lazy(() => import('./pages/CustomerAccounts'));
const CustomerAccountDetailPage = lazy(() => import('./pages/CustomerAccountDetailPage'));
const CustomerRequests = lazy(() => import('./pages/CustomerRequests'));
const Approvals = lazy(() => import('./pages/Approvals'));
const OperationsDashboard = lazy(() => import('./pages/OperationsDashboard'));
const WorkflowDashboard = lazy(() => import('./pages/WorkflowDashboard'));
const ExecutiveDashboard = lazy(() => import('./pages/ExecutiveDashboard'));
const CeoDashboardPage = lazy(() => import('./pages/analytics/CeoDashboardPage'));
const FinanceSettings = lazy(() => import('./pages/FinanceSettings'));
const EODSettings = lazy(() => import('./pages/EODSettings'));
const DailyReconciliation = lazy(() => import('./pages/DailyReconciliation'));
const CollectionsWorklist = lazy(() => import('./pages/CollectionsWorklist'));
const ARClassificationDashboard = lazy(() => import('./pages/ARClassificationDashboard'));
const ZoneManagement = lazy(() => import('./pages/ZoneManagement'));
const SalesPipelinePage = lazy(() => import('./pages/commercial/SalesPipelinePage'));
const OnboardingWizardPage = lazy(() => import('./pages/commercial/OnboardingWizardPage'));
const CustomerDetailPage = lazy(() => import('./pages/commercial/CustomerDetailPage'));
const CommercialSalesDashboard = lazy(() => import('./pages/commercial/CommercialSalesDashboard'));
const SalesManagerDashboard = lazy(() => import('./pages/dashboard/SalesManagerDashboard'));
const MyPortfolioPage = lazy(() => import('./pages/commercial/MyPortfolioPage'));
const PortfolioAssignmentPage = lazy(() => import('./pages/commercial/admin/PortfolioAssignmentPage'));
const SalesAdminDashboard = lazy(() => import('./pages/commercial/admin/SalesAdminDashboard'));
const ApprovalDashboardPage = lazy(() => import('./pages/commercial/ApprovalDashboardPage'));
const DiscountApprovalPage = lazy(() => import('./pages/commercial/DiscountApprovalPage'));
const ApprovalReviewPage = lazy(() => import('./pages/commercial/ApprovalReviewPage'));
const TerritoryMapPage = lazy(() => import('./pages/commercial/TerritoryMapPage'));
const MyLeadsPage = lazy(() => import('./pages/commercial/MyLeadsPage'));
const LeadDetailPage = lazy(() => import('./pages/commercial/LeadDetailPage'));
const NewLeadPage = lazy(() => import('./pages/commercial/NewLeadPage'));
const MatchDetailPage = lazy(() => import('./pages/commercial/MatchDetailPage'));
const ScoutPerformancePage = lazy(() => import('./pages/commercial/ScoutPerformancePage'));
const MissingGPSListPage = lazy(() => import('./pages/commercial/MissingGPSListPage'));
const ScoutTasksPage = lazy(() => import('./pages/commercial/ScoutTasksPage'));
const LeadReviewPage = lazy(() => import('./pages/commercial/LeadReviewPage'));
const ManagerInboxPage = lazy(() => import('./pages/commercial/ManagerInboxPage'));
const AssignedLeadsPage = lazy(() => import('./pages/commercial/AssignedLeadsPage'));
const ContractListPage = lazy(() => import('./pages/commercial/ContractListPage'));
const ContractGenerationPage = lazy(() => import('./pages/commercial/ContractGenerationPage'));
const ContractUpdatePage = lazy(() => import('./pages/commercial/ContractUpdatePage'));
const UpsellQueuePage = lazy(() => import('./pages/commercial/UpsellQueuePage'));
const UpsellReviewPage = lazy(() => import('./pages/commercial/UpsellReviewPage'));
const SearchCustomerForUpsell = lazy(() => import('./pages/commercial/SearchCustomerForUpsell'));
const CustomerDetailForUpsell = lazy(() => import('./pages/commercial/CustomerDetailForUpsell'));
const ReportSurplusPage = lazy(() => import('./pages/commercial/ReportSurplusPage'));
const ProposeTermsPage = lazy(() => import('./pages/commercial/ProposeTermsPage'));
const CrossBorderReportPage = lazy(() => import('./pages/commercial/CrossBorderReportPage'));
const CrossBorderReportsPage = lazy(() => import('./pages/commercial/CrossBorderReportsPage'));
const TSODashboard = lazy(() => import('./pages/logistics/TSODashboard'));
const CompletedCustomersReport = lazy(() => import('./pages/logistics/CompletedCustomersReport'));
const TransportationCommerceDashboard = lazy(() => import('./pages/transportation/TransportationCommerceDashboard'));
const SiteCheckPage = lazy(() => import('./pages/logistics/SiteCheckPage'));
const SiteCheckReportPage = lazy(() => import('./pages/logistics/SiteCheckReportPage'));
const SiteCheckCenterPage = lazy(() => import('./pages/logistics/SiteCheckCenterPage'));
const CompletedCustomerDetailPage = lazy(() => import('./pages/logistics/CompletedCustomerDetailPage'));
const TransportOrgManagement = lazy(() => import('./pages/transportation/TransportOrgManagement'));
const MyTeamToday = lazy(() => import('./pages/transportation/MyTeamToday'));
const TransportDivisionSummary = lazy(() => import('./pages/transportation/TransportDivisionSummary'));
const TransportOverview = lazy(() => import('./pages/transportation/TransportOverview'));
const FleetDashboard = lazy(() => import('./pages/fleet/FleetDashboard'));
const TruckDetailPage = lazy(() => import('./pages/fleet/TruckDetailPage'));
const PJPManagement = lazy(() => import('./pages/fleet/PJPManagement'));
const PJPDetailPage = lazy(() => import('./pages/fleet/PJPDetailPage'));
const PlanCalendarPage = lazy(() => import('./pages/fleet/PlanCalendarPage'));
const PlanDetailPage = lazy(() => import('./pages/fleet/PlanDetailPage'));
const UnassignedAccounts = lazy(() => import('./pages/fleet/UnassignedAccounts'));
const CrewAttendance = lazy(() => import('./pages/fleet/CrewAttendance'));  // V17.474 item 4
const ScoutCapture = lazy(() => import('./pages/scout/ScoutCapture'));
const ScoutDashboard = lazy(() => import('./pages/scout/ScoutDashboard'));
const ScoutLeadEdit = lazy(() => import('./pages/scout/ScoutLeadEdit'));
const LeadAllocation = lazy(() => import('./pages/scout/LeadAllocation'));
const ScoutPerformance = lazy(() => import('./pages/scout/ScoutPerformance'));
const JourneyHistory = lazy(() => import('./pages/scout/JourneyHistory'));
const ResidentialConversionPage = lazy(() => import('./pages/residential/ResidentialConversionPage'));
const VerificationQueuePage = lazy(() => import('./pages/residential/VerificationQueuePage'));
const TeamDashboardPage = lazy(() => import('./pages/residential/TeamDashboardPage'));
const ResidentialCustomerDetail = lazy(() => import('./pages/ResidentialCustomerDetail'));
const ResidentialAmendmentQueue = lazy(() => import('./pages/ResidentialAmendmentQueue'));
const ResidentialDashboard = lazy(() => import('./pages/residential/ResidentialDashboard'));
const TeamLeaderDashboard = lazy(() => import('./pages/residential/TeamLeaderDashboard'));
const AcquisitionFeed = lazy(() => import('./pages/residential/AcquisitionFeed'));
const ActivityTracker = lazy(() => import('./pages/residential/ActivityTracker'));
const VerificationDashboard = lazy(() => import('./pages/residential/VerificationDashboard'));
const ClosureApprovalQueue = lazy(() => import('./pages/residential/ClosureApprovalQueue'));
const FileUpload = lazy(() => import('./pages/analytics/FileUpload'));
const CashKPIDashboard = lazy(() => import('./pages/analytics/CashKPIDashboard'));
const PaidByMonth = lazy(() => import('./pages/analytics/PaidByMonth'));
const PaidByCAC = lazy(() => import('./pages/analytics/PaidByCAC'));
const UnpaidByCAC = lazy(() => import('./pages/analytics/UnpaidByCAC'));
const PaidByContribution = lazy(() => import('./pages/analytics/PaidByContribution'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminRepDetailPage = lazy(() => import('./pages/admin/AdminRepDetailPage'));
const GeographyManagement = lazy(() => import('./pages/admin/GeographyManagement'));
const TaxonomyManagement = lazy(() => import('./pages/admin/TaxonomyManagement'));
const RoleManagement = lazy(() => import('./pages/admin/RoleManagement'));
const PermissionsManagement = lazy(() => import('./pages/admin/PermissionsManagement'));
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'));
const LocationManagementPage = lazy(() => import('./pages/admin/LocationManagementPage'));
const CommercialTeamsManagement = lazy(() => import('./pages/admin/CommercialTeamsManagement'));
const DepartmentsManagement = lazy(() => import('./pages/admin/DepartmentsManagement'));
const DiscountApprovalSettings = lazy(() => import('./pages/admin/DiscountApprovalSettings'));
const ServiceCatalogManagement = lazy(() => import('./pages/admin/ServiceCatalogManagement'));
const PriceBookManagement = lazy(() => import('./pages/finance/PriceBookManagement'));
const ResidentialPackageManagement = lazy(() => import('./pages/finance/ResidentialPackageManagement'));
const ExceptionQueue = lazy(() => import('./pages/finance/ExceptionQueue'));
const CashierDashboard = lazy(() => import('./pages/finance/CashierDashboard'));
const ARDashboard = lazy(() => import('./pages/finance/ARDashboard'));
const ARCommercialLeaderDashboard = lazy(() => import('./pages/ar/ARCommercialLeaderDashboard'));
const ARCommercialOfficerWorkspace = lazy(() => import('./pages/ar/ARCommercialOfficerWorkspace'));
const MyPortfolioRoute = lazy(() => import('./pages/ar/MyPortfolioRoute'));
const ARCommercialPortfolioAssignment = lazy(() => import('./pages/ar/ARCommercialPortfolioAssignment'));
const ARCommercialCollectionTargets = lazy(() => import('./pages/ar/ARCommercialCollectionTargets'));
const Customer360Page = lazy(() => import('./pages/ar/Customer360Page'));
const SLADashboardPage = lazy(() => import('./pages/ar/SLADashboardPage'));
const ServiceStopsPage = lazy(() => import('./pages/ar/ServiceStopsPage'));
const VillageZonesPage = lazy(() => import('./pages/ar/VillageZonesPage'));
const ARAccountManagementKPIs = lazy(() => import('./pages/ar/ARAccountManagementKPIs'));
const ARTeamPerformancePage = lazy(() => import('./pages/ar/ARTeamPerformancePage'));
const ArLeaderDashboard = lazy(() => import('./pages/ar/ArLeaderDashboard'));
const ArOfficerWorkspace = lazy(() => import('./pages/ar/ArOfficerWorkspace'));
const ARResidentialOfficerWorkspace = lazy(() => import('./pages/ar/ARResidentialOfficerWorkspace'));
const ResidentialPaymentReport = lazy(() => import('./pages/ar/ResidentialPaymentReport'));
const ARResidentialDailyCalls = lazy(() => import('./pages/ar/ARResidentialDailyCalls'));
const ARResidentialCallPlan = lazy(() => import('./pages/ar/ARResidentialCallPlan'));
const ARResidentialOfficerDetail = lazy(() => import('./pages/ar/ARResidentialOfficerDetail'));
const ARResidentialLeaderDashboard = lazy(() => import('./pages/ar/ARResidentialLeaderDashboard'));
const ARResidentialPortfolioAssignment = lazy(() => import('./pages/ar/ARResidentialPortfolioAssignment'));
const AROrgManagement = lazy(() => import('./pages/ar/AROrgManagement'));  // V17.363 AR org admin
const ARResidentialCollectionTargets = lazy(() => import('./pages/ar/ARResidentialCollectionTargets'));
const ARResidentialCallSetupPage = lazy(() => import('./pages/ar/ARResidentialCallSetupPage'));
const ARResidentialTeamBoardRoute = lazy(() => import('./pages/ar/ARResidentialTeamBoardRoute'));
const ARResidentialMyTeam = lazy(() => import('./pages/ar/ARResidentialMyTeam'));
const ARResidentialPerformanceReport = lazy(() => import('./pages/ar/ARResidentialPerformanceReport'));
const ARCommercialTeamBoardRoute = lazy(() => import('./pages/ar/ARCommercialTeamBoardRoute'));
const QuoteTemplateManagement = lazy(() => import('./pages/admin/QuoteTemplateManagement'));
const ContractSettings = lazy(() => import('./pages/admin/ContractSettings'));
const ContractTemplateEditor = lazy(() => import('./pages/admin/ContractTemplateEditor'));
const ConfirmationLetterTemplateEditor = lazy(() => import('./pages/admin/ConfirmationLetterTemplateEditor'));
const TagManagement = lazy(() => import('./pages/admin/TagManagement'));
const QuickBooksSettings = lazy(() => import('./pages/admin/QuickBooksSettings'));
const SyncMonitor = lazy(() => import('./pages/admin/SyncMonitor'));
const EODITDashboard = lazy(() => import('./pages/admin/EODITDashboard'));
const NotificationInbox = lazy(() => import('./pages/NotificationInbox'));
const ARCommercialWorkspace = lazy(() => import('./pages/ar/CommercialWorkspace'));
const ARCommercialMyTeam = lazy(() => import('./pages/ar/ARCommercialMyTeam'));
const ARDailyTasks = lazy(() => import('./pages/ar/ARDailyTasks'));
const AREscalationQueue = lazy(() => import('./pages/ar/AREscalationQueue'));
const ARKPIDashboard = lazy(() => import('./pages/ar/ARKPIDashboard'));
const CommercialPortfolioPage = lazy(() => import('./pages/ar/CommercialPortfolioPage'));
const ContractManagementPage = lazy(() => import('./pages/ar/ContractManagementPage'));
const MyReferralsDashboard = lazy(() => import('./pages/referrals/MyReferralsDashboard'));
const ReferralWorkspace = lazy(() => import('./pages/referrals/ReferralWorkspace'));
const ReferClientPage = lazy(() => import('./pages/referrals/ReferClientPage'));
const ReferralDetailPage = lazy(() => import('./pages/referrals/ReferralDetailPage'));
const DriverRouteView = lazy(() => import('./pages/operations/DriverRouteView'));
const IncentiveSettings = lazy(() => import('./pages/admin/IncentiveSettings'));
const ReportsHub = lazy(() => import('./pages/reports/ReportsHub'));
const ScoutLeadPerformancePage = lazy(() => import('./pages/reports/ScoutLeadPerformancePage'));
const ScoutReportPage = lazy(() => import('./pages/reports/ScoutReportPage'));
const SalesReportPage = lazy(() => import('./pages/reports/SalesReportPage'));
const LeadSourceReportPage = lazy(() => import('./pages/reports/LeadSourceReportPage'));
const ReferralsReportPage = lazy(() => import('./pages/reports/ReferralsReportPage'));
const LeadConversionDetailPage = lazy(() => import('./pages/reports/LeadConversionDetailPage'));
const LeadsToSalesDetailPage = lazy(() => import('./pages/reports/LeadsToSalesDetailPage'));
const SalesOverviewPage = lazy(() => import('./pages/reports/SalesOverviewPage'));
const SalesTeamPerformancePage = lazy(() => import('./pages/reports/SalesTeamPerformancePage'));
const SalesPerformanceByTeamPage = lazy(() => import('./pages/reports/SalesPerformanceByTeamPage'));
const SalesPerformanceByRepPage = lazy(() => import('./pages/reports/SalesPerformanceByRepPage'));
const SalesPipelineByRepReport = lazy(() => import('./pages/reports/SalesPipelineByRepReport'));
const SalesPlanIndividualPage = lazy(() => import('./pages/reports/SalesPlanIndividualPage'));
const ARCollectionReportPage = lazy(() => import('./pages/reports/ARCollectionReportPage'));
const ARCustomerGradePage = lazy(() => import('./pages/reports/ARCustomerGradePage'));  // V17.124
const ARResidentialOfficerPerformanceReport = lazy(() => import('./pages/reports/ARResidentialOfficerPerformanceReport'));  // V17.526
const ARSiteVisitReportPage = lazy(() => import('./pages/reports/ar-site-visits/ARSiteVisitReportPage'));  // row 98b
const ResidentialConversionReportPage = lazy(() => import('./pages/reports/ResidentialConversionReportPage'));
const BillingSummaryReportPage = lazy(() => import('./pages/reports/BillingSummaryReportPage'));
const CustomerStatementPage = lazy(() => import('./pages/reports/CustomerStatementPage'));
const SalesConversionReportPage = lazy(() => import('./pages/reports/SalesConversionReportPage'));
const WorkJourneyPage = lazy(() => import('./pages/reports/WorkJourneyPage'));
const MemberSalesDetailPage = lazy(() => import('./pages/reports/MemberSalesDetailPage'));
const ServiceTransportationReportPage = lazy(() => import('./pages/reports/ServiceTransportationReportPage'));
const GarbageCollectionByMonthPage = lazy(() => import('./pages/reports/GarbageCollectionByMonthPage'));
const SoftDeleteReportPage = lazy(() => import('./pages/reports/SoftDeleteReportPage'));
const MyTrainingPage = lazy(() => import('./pages/training/MyTrainingPage'));
const CourseViewerPage = lazy(() => import('./pages/training/CourseViewerPage'));
const CourseManagementPage = lazy(() => import('./pages/training/CourseManagementPage'));
const CourseEditorPage = lazy(() => import('./pages/training/CourseEditorPage'));
const AssignmentManagementPage = lazy(() => import('./pages/training/AssignmentManagementPage'));
const DataManagementPage = lazy(() => import('./pages/admin/DataManagementPage'));
const CSDashboard = lazy(() => import('./pages/customerService/CSDashboard'));
const CSTicketList = lazy(() => import('./pages/customerService/CSTicketList'));
const CSTicketDetail = lazy(() => import('./pages/customerService/CSTicketDetail'));
const CSNewTicket = lazy(() => import('./pages/customerService/CSNewTicket'));
const CSMyTickets = lazy(() => import('./pages/customerService/CSMyTickets'));
const CSAdminSettings = lazy(() => import('./pages/customerService/CSAdminSettings'));
const CSReports = lazy(() => import('./pages/customerService/CSReports'));
// V17.263 AR Residential My Team
// V17.359 AR Residential - Performance Report (period presets + staff filter)
// V17.299 AR Commercial - Daily Team Board (page + ?tv=1 wall display)
// V17.301 AR Residential - Customer Call Performance by Staff (leader report)

// V9.5 Quote Template Management (Admin Only)

// V17.17.0 Contract Settings & Template Management (Admin Only)

// V17.21.0 Tag Management (Admin Only)

// QuickBooks Integration Settings (Admin Only)

// Sync Health Monitor (Admin Only)

// V17.68.1 EOD IT Hub (Admin Only)

// V17.8 Notification Inbox (All authenticated users)

// V17.23.0 AR Commercial Workspace Pages (Team Leader)
// V17.29 AR Commercial My Team
// V17.86 AR Module Pages

// V17.32 Internal Referral Program
// V17.32.1 Referral Workspace (Sales Admin)
// V17.42 Full-page Refer a Client form (mobile-first)
// V17.43 Referral Detail Page (native mobile app style)
// V17.32.1 Driver Route View (Excess Garbage Reporting)
// V17.32.3 Incentive Settings (Admin)

// V17.44+ Reports Module
// V17.77 Member Sales Detail (drill-down from team leader dashboard)
// V17.69 Services & Transportation Report
// V17.79 System Admin Reports

// V17.54 Online Training Module

// V17.55 Data Audit Management (Admin)

// V17.246 Customer Service module


// V17.520 (Phase 0c, brief 2026-10-26-001, F20): on phones toasts sit below the fixed 64 px
// MobileHeader and the top safe area instead of covering them; in dark mode they are dark.
// Desktop keeps the library's default 16 px top offset.
export function AppToaster() {
  const isDesktop = useIsDesktop();
  return (
    <Toaster
      containerStyle={isDesktop ? undefined : { top: 'calc(env(safe-area-inset-top) + 72px)' }}
      position="top-center"
      toastOptions={{
        duration: 3000,
        className: 'dark:!bg-gray-800 dark:!text-gray-100',
        style: {
          maxWidth: '90vw',
          padding: '12px 16px',
          borderRadius: '12px',
          fontSize: '14px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
        success: {
          iconTheme: { primary: '#10b981', secondary: '#fff' },
          style: { border: '1px solid #d1fae5' },
        },
        error: {
          duration: 5000,
          iconTheme: { primary: '#ef4444', secondary: '#fff' },
          style: { border: '1px solid #fecaca' },
        },
      }}
    />
  );
}

function App() {
  return (
    <AuthProvider>
      <LayoutProvider>
        <LanguageProvider>
        <ThemeProvider>
        <AppToaster />
        <ChunkErrorBoundary>
        <Suspense fallback={<BootLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/select-role" element={<SelectRole />} />  {/* V17.71 Multi-Role Selector */}
          {/* V17.262 Blocker 1 fix: this root element used to be a plain
              `<ProtectedRoute><Layout /></ProtectedRoute>`, which wraps EVERY
              nested route below (via <Outlet/>) and redirected to /login
              before React Router ever descended far enough to reach the
              `ar/residential/team-board` leaf route. That meant an
              unauthenticated paired TV could never reach the wall display,
              no matter what the leaf route's own element did. `AppRootGate`
              carves out exactly one unauthenticated escape hatch — the TV's
              own URL — and is a no-op passthrough (identical behavior) for
              every other request. See ARResidentialTeamBoardRoute.tsx for
              the full reasoning. */}
          <Route
            path="/"
            element={<AppRootGate />}
          >
          {/* V7.2 P0 FIX: Smart redirect to user's default workspace instead of hard-coded /dashboard */}
          <Route index element={<DefaultRouteRedirect />} />

          {/* V17.8 Notification Inbox - accessible to all authenticated users */}
          <Route path="notifications" element={<NotificationInbox />} />

          {/* V17.70: Sales Manager Dashboard */}
          <Route
            path="dashboard/sales-manager"
            element={
              <ProtectedRoute requirePermission="approvals.price.discount">
                <SalesManagerDashboard />
              </ProtectedRoute>
            }
          />

          {/* V17.69: Sales Team Leader - Commercial Dashboard */}
          <Route
            path="dashboard/sales-team-leader-commercial"
            element={
              <ProtectedRoute requirePermission="commercial.leads.assign">
                <CommercialSalesDashboard />
              </ProtectedRoute>
            }
          />

          {/* V7.2 P0 Fix: Protect Admin & Ops Workspace dashboard */}
          <Route
            path="dashboard"
            element={
              <ProtectedRoute requirePermission="workspace.admin_ops.access">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="customers"
            element={
              <ProtectedRoute requireAnyPermission={['customers.sync', 'users.edit']}>
                <Customers />
              </ProtectedRoute>
            }
          />
          <Route
            path="invoices"
            element={
              <ProtectedRoute requireAnyPermission={['users.edit']}>
                <Invoices />
              </ProtectedRoute>
            }
          />
          <Route
            path="sync"
            element={
              <ProtectedRoute requireAnyPermission={['customers.sync', 'admin.system.manage']}>
                <Sync />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/operations"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <OperationsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/workflow"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <WorkflowDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/executive"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <ExecutiveDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/ceo"
            element={
              <ProtectedRoute requirePermission="analytics.view">
                <CeoDashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Advanced Reporting Module Routes (Finance Admin + Admin) */}
          <Route
            path="analytics/upload"
            element={
              <ProtectedRoute requirePermission="admin.system.manage">
                <FileUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/kpi"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <CashKPIDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/paid-month"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <PaidByMonth />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/paid-cac"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <PaidByCAC />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/unpaid-cac"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <UnpaidByCAC />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics/contribution"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'admin.reports.view']}>
                <PaidByContribution />
              </ProtectedRoute>
            }
          />

          {/* Customer Management Routes */}
          <Route
            path="customer-accounts"
            element={
              /* V17.325: gated on ACCOUNT_READ_PERMISSIONS, mirroring the backend
                 guard in customer_accounts.py. `customer_accounts.view` (V17.324)
                 replaces the `ar.dashboard.{officer,leader}.view` stand-in V17.321
                 had to add here — a DASHBOARD permission was never what "may read
                 the account book" means. The write codes stay because anyone who
                 may modify an account may read it, and because that set is derived
                 from live permissions rather than the V17.324 seed list.
                 Read-only is still enforced separately: CustomerAccounts.tsx gates
                 row edit/delete on `canManageAccounts`. */
              <ProtectedRoute requireAnyPermission={[...ACCOUNT_READ_PERMISSIONS]}>
                <CustomerAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="customer-accounts/:accountId"
            element={
              /* V17.325: same list as above plus `ar.portfolio.assign`, which the
                 detail route has admitted since V17.28 for the assignment flow. */
              <ProtectedRoute requireAnyPermission={[...ACCOUNT_READ_PERMISSIONS, 'ar.portfolio.assign']}>
                <CustomerAccountDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="customer-requests"
            element={
              <ProtectedRoute requireAnyPermission={['commercial.pipeline.view', 'commercial.onboarding.view', 'users.edit']}>
                <CustomerRequests />
              </ProtectedRoute>
            }
          />

          {/* V17.82: Unified Approval Queue */}
          <Route
            path="commercial/approvals"
            element={
              <ProtectedRoute requireAnyPermission={['commercial.onboarding.verify', 'commercial.onboarding.ar_approve', 'users.edit']}>
                <Approvals />
              </ProtectedRoute>
            }
          />

          {/* V14.0 Dual Approval Workflow - Separate Queues (legacy, kept for backward compat) */}
          <Route
            path="approvals/commercial/sales-admin"
            element={
              <ProtectedRoute requiredRoles={['sales_admin_commercial', 'admin']}>
                <Approvals queueType="sales_admin" />
              </ProtectedRoute>
            }
          />
          <Route
            path="approvals/commercial/ar-officer"
            element={
              <ProtectedRoute requirePermission="commercial.onboarding.ar_approve">
                <Approvals queueType="ar_officer" />
              </ProtectedRoute>
            }
          />

          {/* V17.60 Full-page Approval Review */}
          <Route
            path="approvals/commercial/sales-admin/review/:accountId"
            element={
              <ProtectedRoute requiredRoles={['sales_admin_commercial', 'admin']}>
                <ApprovalReviewPage queueType="sales_admin" />
              </ProtectedRoute>
            }
          />
          <Route
            path="approvals/commercial/ar-officer/review/:accountId"
            element={
              <ProtectedRoute requirePermission="commercial.onboarding.ar_approve">
                <ApprovalReviewPage queueType="ar_officer" />
              </ProtectedRoute>
            }
          />

          {/* Legacy /approvals route - redirect to sales-admin queue */}
          <Route
            path="approvals"
            element={<Navigate to="/commercial/approvals" replace />}
          />
          <Route
            path="users"
            element={
              <ProtectedRoute requirePermission="users.edit">
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/finance"
            element={
              <ProtectedRoute requirePermission="admin.pricing.manage">
                <FinanceSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings/eod"
            element={
              <ProtectedRoute requirePermission="admin.system.manage">
                <EODSettings />
              </ProtectedRoute>
            }
          />

          {/* EOD Automation Routes */}
          <Route
            path="reconciliation"
            element={
              <ProtectedRoute requireAnyPermission={['reconciliation.submit', 'reconciliation.view', 'users.edit']}>
                <DailyReconciliation />
              </ProtectedRoute>
            }
          />
          <Route
            path="collections"
            element={
              <ProtectedRoute requireAnyPermission={['ar_classification.view', 'users.edit']}>
                <CollectionsWorklist />
              </ProtectedRoute>
            }
          />
          <Route
            path="ar-classification"
            element={
              <ProtectedRoute requireAnyPermission={['price_book.manage', 'users.edit']}>
                <ARClassificationDashboard />
              </ProtectedRoute>
            }
          />

          {/* Zone Management Routes (V5.0 Geographic Foundation) */}
          <Route
            path="zone-management"
            element={
              <ProtectedRoute requirePermission="admin.reference_data.manage">
                <ZoneManagement />
              </ProtectedRoute>
            }
          />

          {/* V17.19.0 Contract List Page (AR Officer, Sales Manager, Admin) */}
          <Route
            path="contracts"
            element={
              <ProtectedRoute requireAnyPermission={['contracts.view', 'users.edit']}>
                <ContractListPage />
              </ProtectedRoute>
            }
          />

          {/* V17.16.69: Consolidated Approvals Routes */}
          {/* V17.16.70: Use specific approvals.price.discount permission */}
          <Route
            path="approvals/discounts"
            element={
              <ProtectedRoute requireAnyPermission={['approvals.price.discount', 'users.edit']}>
                <DiscountApprovalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="approvals/sales"
            element={
              <ProtectedRoute requireAnyPermission={['approvals.verify', 'approvals.approve', 'users.edit']}>
                <ApprovalDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="approvals/finance"
            element={
              <ProtectedRoute requireAnyPermission={['analytics.view', 'users.edit']}>
                <ARClassificationDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="approvals/transport"
            element={
              <ProtectedRoute requireAnyPermission={['transportation.approve', 'logistics.verify', 'users.edit']}>
                <TransportationCommerceDashboard />
              </ProtectedRoute>
            }
          />

          {/* V17.64 Admin Dashboard Hub */}
          <Route
            path="admin/dashboard"
            element={
              <ProtectedRoute requirePermission="admin.reports.view">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/dashboard/rep/:userId"
            element={
              <ProtectedRoute requirePermission="admin.reports.view">
                <AdminRepDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Geographic Master Data Management (V6.0 CRITICAL P1 UAT FIX) */}
          <Route
            path="admin/geography"
            element={
              <ProtectedRoute requirePermission="admin.reference_data.manage">
                <GeographyManagement />
              </ProtectedRoute>
            }
          />

          {/* Taxonomy Management (V7.0 Dynamic Industry/Segment Management) */}
          <Route
            path="admin/taxonomy"
            element={
              <ProtectedRoute requirePermission="admin.reference_data.manage">
                <TaxonomyManagement />
              </ProtectedRoute>
            }
          />

          {/* Role Management (V7.0 Dynamic RBAC - Permission Editor) */}
          <Route
            path="admin/roles"
            element={
              <ProtectedRoute requirePermission="users.edit">
                <RoleManagement />
              </ProtectedRoute>
            }
          />

          {/* Permissions Management (V16.15 - User Management Group) */}
          <Route
            path="admin/permissions"
            element={
              <ProtectedRoute requirePermission="users.edit">
                <PermissionsManagement />
              </ProtectedRoute>
            }
          />

          {/* Audit Log (V8.0 UAT - Unified Audit Log) */}
          <Route
            path="admin/audit-log"
            element={
              <ProtectedRoute requirePermission="admin.reports.view">
                <AuditLogPage />
              </ProtectedRoute>
            }
          />

          {/* Location Data Governance (V16.9 - District Visibility Management) */}
          <Route
            path="admin/settings/locations"
            element={
              <ProtectedRoute requirePermission="admin.reference_data.manage">
                <LocationManagementPage />
              </ProtectedRoute>
            }
          />

          {/* V17.0: Departments Management (Admin) */}
          <Route
            path="admin/departments"
            element={
              <ProtectedRoute requirePermission="admin.reference_data.manage">
                <DepartmentsManagement />
              </ProtectedRoute>
            }
          />

          {/* V16.15/V17.1: Teams Management (Admin) - Renamed from Commercial Teams */}
          <Route
            path="admin/teams"
            element={
              <ProtectedRoute requirePermission="admin.teams.manage">
                <CommercialTeamsManagement />
              </ProtectedRoute>
            }
          />

          {/* QuickBooks Integration Settings (Admin Only) */}
          <Route
            path="admin/quickbooks-settings"
            element={
              <ProtectedRoute requirePermission="admin.system.manage">
                <QuickBooksSettings />
              </ProtectedRoute>
            }
          />

          {/* V8.2 Finance Workspace Routes (Segregation of Duties) */}
          {/* V8.2 SoD: Service Catalog moved to Admin workspace */}
          <Route
            path="admin/service-catalog"
            element={
              <ProtectedRoute requirePermission="service_catalog.manage">
                <ServiceCatalogManagement />
              </ProtectedRoute>
            }
          />
          {/* V9.5 Quote Template Management (Admin workspace) */}
          <Route
            path="admin/quote-templates"
            element={
              <ProtectedRoute requirePermission="admin.templates.manage">
                <QuoteTemplateManagement />
              </ProtectedRoute>
            }
          />
          {/* V17.17.0 Contract Settings & Template Management (Admin workspace) */}
          <Route
            path="admin/contract-settings"
            element={
              <ProtectedRoute requirePermission="admin.templates.manage">
                <ContractSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/contract-templates"
            element={
              <ProtectedRoute requirePermission="admin.templates.manage">
                <ContractTemplateEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/confirmation-letter-templates"
            element={
              <ProtectedRoute requirePermission="admin.templates.manage">
                <ConfirmationLetterTemplateEditor />
              </ProtectedRoute>
            }
          />
          {/* V17.21.0 Tag Management (Admin workspace) */}
          <Route
            path="admin/tags"
            element={
              <ProtectedRoute requirePermission="admin.reference_data.manage">
                <TagManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/sync-monitor"
            element={
              <ProtectedRoute requirePermission="admin.system.manage">
                <SyncMonitor />
              </ProtectedRoute>
            }
          />
          {/* V17.68.1 EOD IT Hub (Admin workspace) */}
          <Route
            path="admin/eod-it"
            element={
              <ProtectedRoute requirePermission="admin.system.manage">
                <EODITDashboard />
              </ProtectedRoute>
            }
          />
          {/* V17.16.60 Discount Approval Settings (Admin workspace) */}
          <Route
            path="admin/discount-approval-settings"
            element={
              <ProtectedRoute requirePermission="admin.pricing.manage">
                <DiscountApprovalSettings />
              </ProtectedRoute>
            }
          />
          {/* V17.32.3 Incentive Settings (Admin workspace) - Admin only */}
          <Route
            path="admin/incentives"
            element={
              <ProtectedRoute requirePermission="incentives.manage">
                <IncentiveSettings />
              </ProtectedRoute>
            }
          />
          {/* V17.55: Data Audit Management (Admin / Super Admin) */}
          <Route
            path="admin/data-management"
            element={
              <ProtectedRoute requireAnyPermission={['data_audit.view', 'data_audit.manage', 'users.edit']}>
                <DataManagementPage />
              </ProtectedRoute>
            }
          />
          {/* V8.2 SoD: Price Book remains in Finance workspace */}
          <Route
            path="finance/price-book"
            element={
              <ProtectedRoute requirePermission="price_book.manage">
                <PriceBookManagement />
              </ProtectedRoute>
            }
          />
          {/* V9.0 Exception Queue (Finance Manager + Line Manager) */}
          <Route
            path="finance/exceptions"
            element={
              <ProtectedRoute requirePermission="exceptions.review">
                <ExceptionQueue />
              </ProtectedRoute>
            }
          />
          {/* Financial Control Tower - Cashier Dashboard */}
          <Route
            path="finance/cashier"
            element={
              <ProtectedRoute requirePermission="price_book.manage">
                <CashierDashboard />
              </ProtectedRoute>
            }
          />
          {/* V17.0 AR Dashboard - Accounts Receivable Management */}
          <Route
            path="finance/ar-dashboard"
            element={
              <ProtectedRoute requirePermission="ar.dashboard.view">
                <ARDashboard />
              </ProtectedRoute>
            }
          />
          {/* V17.22.0 AR Team Restructuring - Commercial Leader Dashboard */}
          <Route
            path="ar/commercial/leader"
            element={
              <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                <ARCommercialLeaderDashboard />
              </ProtectedRoute>
            }
          />
          {/* V17.22.0 AR Team Restructuring - Commercial Officer Workspace */}
          <Route
            path="ar/commercial/officer"
            element={
              <ProtectedRoute requirePermission="ar.dashboard.officer.view">
                <ARCommercialOfficerWorkspace />
              </ProtectedRoute>
            }
          />
          {/* V17.22.0 AR Team Restructuring - Portfolio Assignment */}
          <Route
            path="ar/commercial/portfolio-assignment"
            element={
              <ProtectedRoute requirePermission="ar.portfolio.assign">
                <ARCommercialPortfolioAssignment />
              </ProtectedRoute>
            }
          />
          {/* V17.22.0 AR Team Restructuring - Collection Targets */}
          <Route
            path="ar/commercial/collection-targets"
            element={
              <ProtectedRoute requirePermission="ar.targets.set">
                <ARCommercialCollectionTargets />
              </ProtectedRoute>
            }
          />
          {/* V17.28 AR Team Performance Dashboard */}
          <Route
            path="ar/commercial/team-performance"
            element={
              <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                <ARTeamPerformancePage />
              </ProtectedRoute>
            }
          />
          {/* V17.299: AR Commercial daily team board. Like the residential
              board, `?tv=1` is handled by AppRootGate above this level so an
              unauthenticated paired TV can still reach the wall display. */}
          <Route
            path="ar/commercial/team-board"
            element={<ARCommercialTeamBoardRoute />}
          />
          {/* V17.23.0 AR Engine - Leader Dashboard */}
          <Route
            path="ar/leader-dashboard"
            element={
              <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                <ArLeaderDashboard />
              </ProtectedRoute>
            }
          />
          {/* V17.23.0 AR Engine - Officer Workspace */}
          <Route
            path="ar/officer-workspace"
            element={
              <ProtectedRoute requirePermission="ar.portfolio.view">
                <ArOfficerWorkspace />
              </ProtectedRoute>
            }
          />
          {/* V17.128 AR Residential - Officer Workspace */}
          <Route
            path="ar/residential/officer"
            element={
              <ProtectedRoute
                requirePermission="ar.dashboard.officer.view"
                requireRoleAssignment="AR Residential Officer"
              >
                <ARResidentialOfficerWorkspace />
              </ProtectedRoute>
            }
          />
          {/* V17.505 AR Field Officer - portfolio map. Permission-gated; the
              API scopes every row to the logged-in officer. */}
          <Route
            path="ar/field/map"
            element={
              <ProtectedRoute requirePermission="ar.field.map.view">
                <Suspense fallback={null}>
                  <ARFieldOfficerMap />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* V17.506 AR leader approval queue for field change requests. The API
              lets only each request's approver act on it. */}
          <Route
            path="ar/field/leader-queue"
            element={
              <ProtectedRoute requirePermission="ar.field.change.approve">
                <Suspense fallback={null}>
                  <ARFieldLeaderQueue />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* V17.212 AR Residential - Payment Report */}
          <Route
            path="ar/residential/officer/payment-report"
            element={
              <ProtectedRoute
                requirePermission="ar.dashboard.officer.view"
                requireRoleAssignment="AR Residential Officer"
              >
                <ResidentialPaymentReport />
              </ProtectedRoute>
            }
          />
          {/* V17.247 AR Residential - Daily Calls */}
          <Route
            path="ar/residential/officer/daily-calls"
            element={
              <ProtectedRoute
                requirePermission="ar.dashboard.officer.view"
                requireRoleAssignment="AR Residential Officer"
              >
                <ARResidentialDailyCalls />
              </ProtectedRoute>
            }
          />
          {/* V17.300 AR Residential - Call Plan */}
          <Route
            path="ar/residential/officer/call-plan"
            element={
              <ProtectedRoute
                requirePermission="ar.dashboard.officer.view"
                requireRoleAssignment="AR Residential Officer"
              >
                <ARResidentialCallPlan />
              </ProtectedRoute>
            }
          />
          {/* V17.261 AR Residential - Officer detail (leader drill-down).
              Opened in a new tab from the leader dashboard's Team Performance
              table. The plural `officers` segment keeps this clear of the
              static `ar/residential/officer[/...]` paths above (V17.229
              route-order shadowing lesson).

              The gate is deliberately coarse: the page is legitimately reached
              by a leader, an admin, or an officer viewing their own id, and
              only the backend can tell those apart from the path param. It
              performs the authoritative self-or-role check and 403s otherwise. */}
          <Route
            path="ar/residential/officers/:officerId"
            element={
              <ProtectedRoute
                requireAnyPermission={[
                  'ar.dashboard.leader.view',
                  'ar.dashboard.officer.view',
                  'users.edit',
                ]}
              >
                <ARResidentialOfficerDetail />
              </ProtectedRoute>
            }
          />
          {/* V17.128 PR 3 AR Residential - Leader Dashboard */}
          <Route
            path="ar/residential/leader"
            element={
              <ProtectedRoute
                requirePermission="ar.dashboard.leader.view"
                requireRoleAssignment="AR Residential Leader"
              >
                <ARResidentialLeaderDashboard />
              </ProtectedRoute>
            }
          />
          {/* V17.262 AR Residential - Daily Team Board (page + ?tv=1 wall display).
              V17.262 Blocker 1 fix: the route element itself decides tv-vs-page
              mode BEFORE the session gate runs, so a paired TV with no session
              can still reach the wall display. See ARResidentialTeamBoardRoute
              for the full reasoning. */}
          <Route
            path="ar/residential/team-board"
            element={<ARResidentialTeamBoardRoute />}
          />
          {/* V17.359 AR Residential - Performance Report. Role-assignment gate
              ONLY, no permission — matches the backend's
              require_role_assignment("ar_residential_leader") on the
              team-performance endpoint exactly, so anyone who can reach the
              page can also load its data. */}
          <Route
            path="ar/residential/performance-report"
            element={
              <ProtectedRoute requireRoleAssignment="AR Residential Leader">
                <ARResidentialPerformanceReport />
              </ProtectedRoute>
            }
          />
          {/* V17.263 AR Residential - My Team (V17.508: grouped by ar_teams) */}
          <Route
            path="ar/residential/team"
            element={
              <ProtectedRoute
                requirePermission="ar.dashboard.leader.view"
                requireRoleAssignment="AR Residential Leader"
              >
                <ARResidentialMyTeam />
              </ProtectedRoute>
            }
          />
          {/* V17.128 PR 3 AR Residential - Portfolio Assignment */}
          {/* V17.174 (bulk-assignment): switched from role-AND-permission to
              permission-only per spec §6.6a. Admin gets ar.portfolio.assign
              via V17.174 grant (see scripts/v17_174_bulk_assignment_batches.sql),
              and dropping the role-name gate lets admin pass without adding a
              second role-name to the guard. Backend endpoints are still gated
              by require_permission("ar.portfolio.assign") so the auth chain
              stays aligned. */}
          <Route
            path="ar/residential/portfolio"
            element={
              <ProtectedRoute requirePermission="ar.portfolio.assign">
                <ARResidentialPortfolioAssignment />
              </ProtectedRoute>
            }
          />
          {/* V17.363 AR Org admin — teams, leaders, officer membership.
              `users.edit` is included so a platform admin can bootstrap the org
              before admin.ar_org.manage has been granted to anyone, matching the
              transport-org precedent. */}
          <Route
            path="ar/admin/org"
            element={
              <ProtectedRoute requireAnyPermission={['admin.ar_org.manage', 'users.edit']}>
                <AROrgManagement />
              </ProtectedRoute>
            }
          />
          {/* V17.128 PR 3 AR Residential - Collection Targets */}
          <Route
            path="ar/residential/targets"
            element={
              <ProtectedRoute
                requirePermission="ar.targets.set"
                requireRoleAssignment="AR Residential Leader"
              >
                <ARResidentialCollectionTargets />
              </ProtectedRoute>
            }
          />
          {/* V17.287 AR Residential - Call Setup (leader-configurable Call Log
              dropdowns). Same gate pair as the Targets page above; backend
              endpoints additionally allow admin. */}
          <Route
            path="ar/residential/call-setup"
            element={
              <ProtectedRoute
                requirePermission="ar.targets.set"
                requireRoleAssignment="AR Residential Leader"
              >
                <ARResidentialCallSetupPage />
              </ProtectedRoute>
            }
          />
          {/* Task 2026-10-27-003 (amendment 2): the call-performance page is retired; its
              per-call list is the Calls tab of the officer performance report. Bookmarks land there. */}
          <Route
            path="ar/residential/reports/call-performance"
            element={<Navigate to="/reports/ar-residential-officer-performance?tab=calls" replace />}
          />
          {/* V9.1 Residential Package Management (Finance Manager) */}
          <Route
            path="finance/residential-packages"
            element={
              <ProtectedRoute requirePermission="manage_residential_packages">
                <ResidentialPackageManagement />
              </ProtectedRoute>
            }
          />

          {/* Part 4: Commercial Workflow Routes (V5.0) */}
          <Route path="commercial">
            {/* V7.0 Sales Performance Dashboard */}
            {/* V17.28.1: Added excludeScouts to block Commercial Scouts from accessing sales rep pages */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.view', 'commercial.onboarding.view', 'users.edit']} excludeScouts>
                  <CommercialSalesDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="pipeline"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.view', 'commercial.pipeline.create', 'users.edit']} excludeScouts>
                  <SalesPipelinePage />
                </ProtectedRoute>
              }
            />
            {/* V11.0: Portfolio Management (My Portfolio - Sales Rep View) */}
            {/* V17.28.1: Added excludeScouts to block Commercial Scouts */}
            <Route
              path="portfolio"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.view', 'users.edit']} excludeScouts>
                  <MyPortfolioPage />
                </ProtectedRoute>
              }
            />
            {/* V17.65: Sales Admin Dashboard Hub */}
            <Route
              path="admin/dashboard"
              element={
                <ProtectedRoute requirePermission="commercial.onboarding.verify">
                  <SalesAdminDashboard />
                </ProtectedRoute>
              }
            />
            {/* V11.0: Portfolio Assignment (Admin/Manager Tool) */}
            <Route
              path="admin/portfolio-assignment"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.onboarding.assign', 'users.edit']}>
                  <PortfolioAssignmentPage />
                </ProtectedRoute>
              }
            />
            {/* V16.1: Approval Dashboard (Admin/Manager Tool) */}
            <Route
              path="approvals"
              element={
                <ProtectedRoute requireAnyPermission={['approvals.verify', 'approvals.approve', 'users.edit']}>
                  <ApprovalDashboardPage />
                </ProtectedRoute>
              }
            />
            {/* V17.16.60: Discount Approval Dashboard (Sales Manager/Admin) */}
            {/* V17.16.70: Updated to use specific approvals.price.discount permission */}
            <Route
              path="discount-approvals"
              element={
                <ProtectedRoute requireAnyPermission={['approvals.price.discount', 'users.edit']}>
                  <DiscountApprovalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="onboarding/:customerId?"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.create', 'commercial.pipeline.edit', 'commercial.onboarding.view', 'commercial.onboarding.verify', 'commercial.onboarding.assign', 'customers.sync', 'users.edit']}>
                  <OnboardingWizardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="customer/:customerId"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.view', 'commercial.pipeline.edit', 'commercial.onboarding.view', 'customers.sync', 'users.edit']}>
                  <CustomerDetailPage />
                </ProtectedRoute>
              }
            />
            {/* V17.59.4: Contract Generation Page (full-page, replaces modal) */}
            <Route
              path="customer/:customerId/contract"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.view', 'commercial.pipeline.edit', 'commercial.onboarding.view', 'customers.sync', 'users.edit']}>
                  <ContractGenerationPage />
                </ProtectedRoute>
              }
            />
            {/* V17.64: Contract Update Page */}
            <Route
              path="customer/:customerId/contract/edit"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.view', 'commercial.pipeline.edit', 'commercial.onboarding.view', 'customers.sync', 'users.edit']}>
                  <ContractUpdatePage />
                </ProtectedRoute>
              }
            />
            {/* V17.63: Upsell Queue */}
            <Route
              path="upsells"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.edit', 'commercial.leads.create', 'commercial.upsell.create', 'commercial.upsell.approve', 'commercial.onboarding.ar_approve', 'logistics.site_check.request', 'users.edit']}>
                  <UpsellQueuePage />
                </ProtectedRoute>
              }
            />
            {/* V17.73: Upsell Surplus Report Flow */}
            <Route
              path="upsells/search"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.edit', 'commercial.leads.create', 'commercial.upsell.create']}>
                  <SearchCustomerForUpsell />
                </ProtectedRoute>
              }
            />
            <Route
              path="upsells/customer/:accountId"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.edit', 'commercial.leads.create', 'commercial.upsell.create']}>
                  <CustomerDetailForUpsell />
                </ProtectedRoute>
              }
            />
            <Route
              path="upsells/report/:accountId"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.edit', 'commercial.leads.create', 'commercial.upsell.create']}>
                  <ReportSurplusPage />
                </ProtectedRoute>
              }
            />
            {/* V17.75: Propose Terms Page */}
            <Route
              path="upsells/:upsellId/propose"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.edit']}>
                  <ProposeTermsPage />
                </ProtectedRoute>
              }
            />
            {/* V17.82: Upsell Review Page */}
            <Route
              path="upsells/:upsellId/review"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.edit', 'commercial.onboarding.verify', 'commercial.upsell.approve', 'commercial.onboarding.ar_approve']}>
                  <UpsellReviewPage />
                </ProtectedRoute>
              }
            />
            {/* V16.4: Commercial Scout Routes */}
            <Route
              path="map"
              element={
                <ProtectedRoute requirePermission="commercial.map.view">
                  <TerritoryMapPage />
                </ProtectedRoute>
              }
            />
            {/* V17.342: Cross-Border Reports */}
            <Route
              path="cross-border/new"
              element={
                <ProtectedRoute requirePermission="commercial.cross_border.create">
                  <CrossBorderReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="cross-border"
              element={
                <ProtectedRoute requirePermission="commercial.cross_border.view">
                  <CrossBorderReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="leads"
              element={
                <ProtectedRoute requirePermission="commercial.leads.view">
                  <MyLeadsPage />
                </ProtectedRoute>
              }
            />
            {/* V17.34: Full-page lead detail view (native mobile-like experience) */}
            <Route
              path="leads/:leadId"
              element={
                <ProtectedRoute requirePermission="commercial.leads.view">
                  <LeadDetailPage />
                </ProtectedRoute>
              }
            />
            {/* V17.30: Full-page lead creation (replaces modal approach) */}
            <Route
              path="leads/new"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.create', 'commercial.leads.create']}>
                  <NewLeadPage />
                </ProtectedRoute>
              }
            />
            {/* V17.68: Match detail page for progressive duplicate check */}
            <Route
              path="leads/new/match-detail"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.pipeline.create', 'commercial.leads.create']}>
                  <MatchDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="scout/performance"
              element={
                <ProtectedRoute requirePermission="commercial.leads.view">
                  <ScoutPerformancePage />
                </ProtectedRoute>
              }
            />
            {/* V16.13: Scout Leader GPS Audit Tool */}
            <Route
              path="scout/leader/missing-gps"
              element={
                <ProtectedRoute requirePermission="commercial.leads.assign">
                  <MissingGPSListPage />
                </ProtectedRoute>
              }
            />
            {/* V16.13.4: Scout "My Tasks" - GPS Audit Assignments */}
            <Route
              path="scout/tasks"
              element={
                <ProtectedRoute requirePermission="commercial.leads.create">
                  <ScoutTasksPage />
                </ProtectedRoute>
              }
            />
            {/* V16.14: Lead Review Dashboard for Team Leaders */}
            <Route
              path="leader/leads"
              element={
                <ProtectedRoute requirePermission="commercial.leads.assign">
                  <LeadReviewPage />
                </ProtectedRoute>
              }
            />
            {/* V16.15: Commercial Manager Inbox - Lead Assignment */}
            <Route
              path="manager/inbox"
              element={
                <ProtectedRoute requirePermission="commercial.leads.assign">
                  <ManagerInboxPage />
                </ProtectedRoute>
              }
            />
            {/* V16.15: Sales Rep Assigned Leads with SLA Timer & Rate Lead */}
            <Route
              path="rep/assigned"
              element={
                <ProtectedRoute requirePermission="commercial.pipeline.view">
                  <AssignedLeadsPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V13.0 Field Force & Logistics Routes */}
          {/* V16.1: Updated to use logistics.dashboard.view permission */}
          <Route path="logistics">
            <Route
              path="tso-dashboard"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.dashboard.view', 'users.edit']}>
                  <TSODashboard />
                </ProtectedRoute>
              }
            />
            {/* V17.16.42: Completed Customers Report */}
            <Route
              path="completed-report"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.dashboard.view', 'users.edit']}>
                  <CompletedCustomersReport />
                </ProtectedRoute>
              }
            />
            {/* V17.59.6: Completed Customer Detail Page */}
            <Route
              path="completed-report/:accountId"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.dashboard.view', 'users.edit']}>
                  <CompletedCustomerDetailPage />
                </ProtectedRoute>
              }
            />
            {/* V17.58.4: TCO Site Check & Journey Plan */}
            <Route
              path="site-checks"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.site_check.manage', 'users.edit']}>
                  <SiteCheckPage />
                </ProtectedRoute>
              }
            />
            {/* V17.59.4: TCO Site Check Report */}
            <Route
              path="site-check-report"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.site_check.manage', 'users.edit']}>
                  <SiteCheckReportPage />
                </ProtectedRoute>
              }
            />
            {/* V17.80: Site Check Center (Site Check Officer allocation) */}
            <Route
              path="site-check-center"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.site_check.allocate', 'users.edit']}>
                  <SiteCheckCenterPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V14.5 Transportation Dashboard Routes */}
          {/* V16.1: Team-specific routes with role-based access control */}
          {/* V17.16: Commercial route now uses SLA-enabled dashboard */}
          {/* V17.58.2: Changed to permission-based access for TCO support */}
          <Route
            path="dashboard/transportation/commerce"
            element={
              <ProtectedRoute requireAnyPermission={['logistics.site_check.manage', 'logistics.dashboard.view', 'users.edit']}>
                <TransportationCommerceDashboard />
              </ProtectedRoute>
            }
          />
          {/* Residential TSO users can only access residential route */}
          <Route
            path="dashboard/transportation/residential"
            element={
              <ProtectedRoute requireAnyPermission={['logistics.site_check.manage', 'logistics.dashboard.view', 'users.edit']}>
                <TSODashboard />
              </ProtectedRoute>
            }
          />

          {/* V17.61 Fleet Management & PJP Routes */}
          <Route path="fleet">
            <Route
              path="dashboard"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.fleet.view', 'logistics.fleet.manage', 'users.edit']}>
                  <FleetDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="trucks/:truckId"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.fleet.manage', 'users.edit']}>
                  <TruckDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="pjp"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.fleet.view', 'logistics.pjp.manage', 'users.edit']}>
                  <PJPManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="pjp/:routeId"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.pjp.manage', 'logistics.fleet.manage', 'users.edit', 'logistics.fleet.view']}>
                  <PJPDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="unassigned"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.fleet.view', 'logistics.pjp.manage', 'users.edit']}>
                  <UnassignedAccounts />
                </ProtectedRoute>
              }
            />
            {/* V17.474 (item 4): Crew attendance — HR & Labour + Head of Service Team (crew.attendance.view) */}
            <Route
              path="crew-attendance"
              element={
                <ProtectedRoute requireAnyPermission={['crew.attendance.view']}>
                  <CrewAttendance />
                </ProtectedRoute>
              }
            />
            {/* V17.368: daily plan calendar + plan detail. The publish BUTTON is
                gated separately on fleet.plan.publish inside PlanDetailPage —
                a route gate does not gate the page's own buttons. */}
            <Route
              path="plans"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.fleet.view', 'logistics.pjp.manage', 'fleet.plan.publish', 'users.edit']}>
                  <PlanCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="plans/:planId"
              element={
                <ProtectedRoute requireAnyPermission={['logistics.fleet.view', 'logistics.pjp.manage', 'fleet.plan.publish', 'users.edit']}>
                  <PlanDetailPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V17.253 Transport Team Workspace Routes */}
          <Route path="transportation">
            <Route
              path="teams/today"
              element={
                <ProtectedRoute
                  requireAnyPermission={[
                    'reports.transport_team.view_own',
                    'reports.transport_team.view_division',
                    'reports.transport_team.view_all',
                    'users.edit',
                  ]}
                >
                  <MyTeamToday />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports/divisions"
              element={
                <ProtectedRoute
                  requireAnyPermission={[
                    'reports.transport_team.view_all',
                    'reports.transport_team.view_division',
                    'users.edit',
                  ]}
                >
                  <TransportDivisionSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports/overview"
              element={
                <ProtectedRoute
                  requireAnyPermission={[
                    'reports.transport_team.view_all',
                    'reports.transport_team.view_division',
                    'users.edit',
                  ]}
                >
                  <TransportOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/org"
              element={
                <ProtectedRoute requireAnyPermission={['admin.transport_org.manage', 'users.edit']}>
                  <TransportOrgManagement />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Part 5: Residential Workflow Routes (V5.0) */}
          <Route path="residential">
            {/* V15.3: Sales Rep Dashboard - "The Scoreboard" (NEW DEFAULT) */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute requireAnyPermission={['residential.convert.create', 'users.edit']}>
                  <ResidentialDashboard />
                </ProtectedRoute>
              }
            />
            {/* Step 1: Lead Conversion (Sales Staff) */}
            <Route
              path="convert"
              element={
                <ProtectedRoute requireAnyPermission={['residential.convert.create', 'residential.convert.view', 'users.edit']}>
                  <ResidentialConversionPage />
                </ProtectedRoute>
              }
            />
            {/* Step 2: Verification Queue (BI Officer) */}
            <Route
              path="queue"
              element={
                <ProtectedRoute requireAnyPermission={['residential.queue.verify', 'users.edit']}>
                  <VerificationQueuePage />
                </ProtectedRoute>
              }
            />
            {/* V16.1: TSO Verification Dashboard (TSO Officers) */}
            <Route
              path="verification"
              element={
                <ProtectedRoute requireAnyPermission={['residential.queue.verify', 'users.edit']}>
                  <VerificationDashboard />
                </ProtectedRoute>
              }
            />
            {/* V16.1: Account Closure Approval Queue (Sales Manager + Finance Manager) */}
            <Route
              path="closure-approvals"
              element={
                <ProtectedRoute requireAnyPermission={['account.closure.sales_approve', 'account.closure.finance_approve', 'users.edit']}>
                  <ClosureApprovalQueue />
                </ProtectedRoute>
              }
            />
            {/* V16.0: Team Leader Command Center (Team Leaders) */}
            <Route
              path="team-dashboard"
              element={
                <ProtectedRoute requireAnyPermission={['residential.team.view', 'users.edit']}>
                  <TeamLeaderDashboard />
                </ProtectedRoute>
              }
            />
            {/* V16.0: Acquisition Feed - Real-Time Ledger */}
            <Route
              path="acquisition-feed"
              element={
                <ProtectedRoute requireAnyPermission={['residential.team.view', 'users.edit']}>
                  <AcquisitionFeed />
                </ProtectedRoute>
              }
            />
            {/* V16.0: Activity Tracker - Performance Report */}
            <Route
              path="activity-tracker"
              element={
                <ProtectedRoute requireAnyPermission={['residential.team.view', 'users.edit']}>
                  <ActivityTracker />
                </ProtectedRoute>
              }
            />
            {/* V16.1: Portfolio Assignment - Residential Admin */}
            <Route
              path="admin/portfolio-assignment"
              element={
                <ProtectedRoute requireAnyPermission={['portfolio.assign.village', 'users.edit']}>
                  <PortfolioAssignmentPage type="residential" />
                </ProtectedRoute>
              }
            />
            {/* Customer Detail Page (All roles with access) */}
            <Route
              path="customers/:customerId"
              element={
                <ProtectedRoute requireAnyPermission={['residential.convert.create', 'residential.convert.view', 'residential.queue.verify', 'residential.team.view', 'users.edit']}>
                  <ResidentialCustomerDetail />
                </ProtectedRoute>
              }
            />
            {/* V9.1: Amendment Queue (Managers) */}
            <Route
              path="amendment-queue"
              element={
                <ProtectedRoute requireAnyPermission={['approve_residential_amendments', 'users.edit']}>
                  <ResidentialAmendmentQueue />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V17.23.0 AR Commercial Workspace Routes (Team Leader) */}
          <Route path="ar-commercial">
            {/* Command Center - Main dashboard */}
            <Route
              path="workspace"
              element={
                <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                  <ARCommercialWorkspace />
                </ProtectedRoute>
              }
            />
            {/* V4.0: Customer 360 Dashboard */}
            <Route
              path="customer-360/:accountId"
              element={
                <ProtectedRoute requireAnyPermission={['ar.dashboard.leader.view', 'ar.dashboard.officer.view', 'commercial.onboarding.ar_approve']}>
                  <Customer360Page />
                </ProtectedRoute>
              }
            />
            {/* V4.0: SLA Dashboard */}
            <Route
              path="sla-dashboard"
              element={
                <ProtectedRoute requireAnyPermission={['ar.dashboard.leader.view']}>
                  <SLADashboardPage />
                </ProtectedRoute>
              }
            />
            {/* V4.0: Service Stops */}
            <Route
              path="service-stops"
              element={
                <ProtectedRoute requireAnyPermission={['ar.dashboard.leader.view', 'ar.dashboard.officer.view', 'commercial.onboarding.ar_approve']}>
                  <ServiceStopsPage />
                </ProtectedRoute>
              }
            />
            {/* V4.0: Village Zones */}
            <Route
              path="zones"
              element={
                <ProtectedRoute requireAnyPermission={['ar.dashboard.leader.view']}>
                  <VillageZonesPage />
                </ProtectedRoute>
              }
            />
            {/* V17.86: My Portfolio - AR Officer's assigned customers */}
            {/* V17.210: MyPortfolioRoute redirects residential officers to their
                own workspace (the commercial endpoints 422 on residential data). */}
            <Route
              path="my-portfolio"
              element={
                <ProtectedRoute requireAnyPermission={['ar.dashboard.officer.view', 'commercial.onboarding.ar_approve', 'ar.dashboard.leader.view']}>
                  <MyPortfolioRoute />
                </ProtectedRoute>
              }
            />
            {/* Portfolio Assignment */}
            <Route
              path="portfolios"
              element={
                <ProtectedRoute requirePermission="ar.portfolio.assign">
                  <ARCommercialPortfolioAssignment />
                </ProtectedRoute>
              }
            />
            {/* Collection Targets */}
            <Route
              path="targets"
              element={
                <ProtectedRoute requirePermission="ar.targets.set">
                  <ARCommercialCollectionTargets />
                </ProtectedRoute>
              }
            />
            {/* V17.28 Account Management KPIs */}
            <Route
              path="account-kpis"
              element={
                <ProtectedRoute requirePermission="ar.targets.set">
                  <ARAccountManagementKPIs />
                </ProtectedRoute>
              }
            />
            {/* Team Performance - redirect to workspace for now */}
            <Route
              path="performance"
              element={
                <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                  <ARCommercialWorkspace />
                </ProtectedRoute>
              }
            />
            {/* V17.29 My Team - Team member list with contact info */}
            <Route
              path="team"
              element={
                <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                  <ARCommercialMyTeam />
                </ProtectedRoute>
              }
            />
            {/* V17.86 AR Module Pages — accept new or existing AR permissions */}
            <Route
              path="daily-tasks"
              element={
                <ProtectedRoute requireAnyPermission={["ar.activities.view", "ar.dashboard.leader.view", "ar.dashboard.officer.view"]}>
                  <ARDailyTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="escalations"
              element={
                <ProtectedRoute requireAnyPermission={["ar.activities.view", "ar.dashboard.leader.view", "ar.dashboard.officer.view"]}>
                  <AREscalationQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="kpis"
              element={
                <ProtectedRoute requireAnyPermission={["ar.activities.view", "ar.dashboard.leader.view", "ar.dashboard.officer.view"]}>
                  <ARKPIDashboard />
                </ProtectedRoute>
              }
            />
            {/* V17.86: Commercial Portfolio - Team Leader Overview */}
            <Route
              path="commercial-portfolio"
              element={
                <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                  <CommercialPortfolioPage />
                </ProtectedRoute>
              }
            />
            {/* Contract Management - Team Leader */}
            <Route
              path="contract-management"
              element={
                <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                  <ContractManagementPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V17.32.1 Operations / Driver Routes - Inside Layout wrapper for sidebar */}
          <Route path="operations">
            <Route
              path="my-route"
              element={
                <ProtectedRoute requirePermission="operations.driver">
                  <DriverRouteView />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V17.44 Reports Module */}
          <Route path="reports">
            <Route
              index
              element={
                <ProtectedRoute requireAnyPermission={['analytics.view', 'reports.scout.view', 'reports.sales.view', 'ar.dashboard.view', 'workspace.transportation.access', 'workspace.admin_ops.access', 'reports.ar_residential_performance.view', 'reports.ar_residential_performance.view_all']}>
                  <ReportsHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="scout"
              element={
                <ProtectedRoute requirePermission="reports.scout.view">
                  <ScoutReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="scout-leads"
              element={
                <ProtectedRoute requirePermission="reports.scout.view">
                  <ScoutLeadPerformancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-pipeline-all"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <SalesReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-overview"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <SalesOverviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-team-performance"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <SalesTeamPerformancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-performance-by-team"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <SalesPerformanceByTeamPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-performance-by-rep"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <SalesPerformanceByRepPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="sales-pipeline-by-rep"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <SalesPipelineByRepReport />
                </ProtectedRoute>
              }
            />
            {/* V17.53: Sales reps can view their own plan via commercial.pipeline.view */}
            <Route
              path="sales-plan-individual"
              element={
                <ProtectedRoute requireAnyPermission={['reports.sales.view', 'commercial.pipeline.view']}>
                  <SalesPlanIndividualPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="lead-sources"
              element={
                <ProtectedRoute requirePermission="reports.scout.view">
                  <LeadSourceReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="referrals"
              element={
                <ProtectedRoute requirePermission="reports.scout.view">
                  <ReferralsReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="lead-conversion"
              element={
                <ProtectedRoute requirePermission="reports.scout.view">
                  <LeadConversionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="leads-to-sales"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <LeadsToSalesDetailPage />
                </ProtectedRoute>
              }
            />
            {/* V17.81 Sales Leader Dashboard */}
            <Route
              path="sales-leader-dashboard"
              element={
                <ProtectedRoute requirePermission="reports.sales.manage">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            {/* V17.76 Sales Conversion Report */}
            <Route
              path="sales-conversion"
              element={
                <ProtectedRoute requirePermission="reports.sales.view">
                  <SalesConversionReportPage />
                </ProtectedRoute>
              }
            />
            {/* V17.78 Work Journey Report */}
            <Route
              path="work-journey"
              element={
                <ProtectedRoute requirePermission="reports.sales.view">
                  <WorkJourneyPage />
                </ProtectedRoute>
              }
            />
            {/* V17.68.1 AR & Collection Report */}
            <Route
              path="ar-collection"
              element={
                <ProtectedRoute requirePermission="ar.dashboard.view">
                  <ARCollectionReportPage />
                </ProtectedRoute>
              }
            />
            {/* Row 98b: field site-visit report — leaders (own teams) / Admin, Executive (all) */}
            <Route
              path="ar-site-visits"
              element={
                <ProtectedRoute requireAnyPermission={['ar.field.visit.report.view', 'ar.field.visit.report.view_all']}>
                  <ARSiteVisitReportPage />
                </ProtectedRoute>
              }
            />
            {/* V17.124 AR Customer Grade Report (Residential) */}
            <Route
              path="ar-residential-grade"
              element={
                <ProtectedRoute requirePermission="ar.dashboard.leader.view">
                  <ARCustomerGradePage />
                </ProtectedRoute>
              }
            />
            {/* V17.526 (task 2026-10-27-003): AR residential officer performance. Either code admits
                the caller; the backend narrows to self / led teams / all and 403s out-of-scope filters. */}
            <Route
              path="ar-residential-officer-performance"
              element={
                <ProtectedRoute requireAnyPermission={['reports.ar_residential_performance.view', 'reports.ar_residential_performance.view_all']}>
                  <ARResidentialOfficerPerformanceReport />
                </ProtectedRoute>
              }
            />
            {/* V17.85: Billing Summary Report */}
            <Route
              path="billing-summary"
              element={
                <ProtectedRoute requireAnyPermission={['ar.dashboard.view', 'reports.view', 'users.edit']}>
                  <BillingSummaryReportPage />
                </ProtectedRoute>
              }
            />
            {/* V17.85: Customer Account Statement */}
            <Route
              path="customer-statement/:customerId"
              element={
                <ProtectedRoute requireAnyPermission={['ar.dashboard.view', 'reports.view', 'users.edit']}>
                  <CustomerStatementPage />
                </ProtectedRoute>
              }
            />
            {/* V17.84: Residential-to-Commercial Conversion Report */}
            <Route
              path="residential-conversion"
              element={
                <ProtectedRoute requirePermission="reports.view">
                  <ResidentialConversionReportPage />
                </ProtectedRoute>
              }
            />
            {/* V17.69 Services & Transportation Report */}
            <Route
              path="service-transportation"
              element={
                <ProtectedRoute requireAnyPermission={['workspace.transportation.access', 'workspace.admin_ops.access']}>
                  <ServiceTransportationReportPage />
                </ProtectedRoute>
              }
            />
            {/* V17.69 Garbage Collection by Month - Surplus Upsell Analysis */}
            <Route
              path="garbage-collection"
              element={
                <ProtectedRoute requireAnyPermission={['reports.sales.view', 'workspace.admin_ops.access', 'workspace.sales.access']}>
                  <GarbageCollectionByMonthPage />
                </ProtectedRoute>
              }
            />
            {/* V17.77 Member Sales Detail (drill-down from team leader dashboard) */}
            <Route
              path="member-sales-detail/:memberId"
              element={
                <ProtectedRoute requireAnyPermission={['commercial.leads.assign', 'reports.sales.view', 'reports.sales.manage']}>
                  <MemberSalesDetailPage />
                </ProtectedRoute>
              }
            />
            {/* V17.79 Soft Delete Report (System Admin) */}
            <Route
              path="soft-deletes"
              element={
                <ProtectedRoute requirePermission="workspace.admin_ops.access">
                  <SoftDeleteReportPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V17.32 Internal Referral Program - Inside Layout wrapper for sidebar */}
          <Route path="referrals">
            <Route
              path="my-dashboard"
              element={
                <ProtectedRoute requirePermission="referrals.create">
                  <MyReferralsDashboard />
                </ProtectedRoute>
              }
            />
            {/* V17.42 Full-page Refer a Client form (mobile-first) */}
            <Route
              path="new"
              element={
                <ProtectedRoute requirePermission="referrals.create">
                  <ReferClientPage />
                </ProtectedRoute>
              }
            />
            {/* V17.43 Referral Detail Page (native mobile app style) */}
            <Route
              path=":id"
              element={
                <ProtectedRoute requirePermission="referrals.create">
                  <ReferralDetailPage />
                </ProtectedRoute>
              }
            />
            {/* V17.32.1 Sales Admin Referral Workspace */}
            <Route
              path="workspace"
              element={
                <ProtectedRoute requirePermission="referrals.manage">
                  <ReferralWorkspace />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* V17.246 Customer Service Routes */}
          <Route path="customer-service">
            {/* V17.506: CS "Waiting for Legacy" queue for field change requests. */}
            <Route
              path="field-changes"
              element={
                <ProtectedRoute requirePermission="ar.field.change.apply">
                  <Suspense fallback={null}>
                    <ARFieldCsQueue />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="dashboard"
              element={
                <ProtectedRoute requireAnyPermission={['cs.tickets.view', 'cs.reports.view']}>
                  <CSDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="tickets"
              element={
                <ProtectedRoute requirePermission="cs.tickets.view">
                  <CSTicketList />
                </ProtectedRoute>
              }
            />
            <Route
              path="tickets/new"
              element={
                /* 2026-10-16-003: V17.303 opened this to every logged-in user; from V17.480
                   that includes every truck driver. cs.tickets.raise is held by every live
                   non-driver role (V17.485). */
                <ProtectedRoute requireAnyPermission={['cs.tickets.raise', 'cs.tickets.create', 'cs.tickets.manage']}>
                  <CSNewTicket />
                </ProtectedRoute>
              }
            />
            <Route
              path="tickets/:ticketId"
              element={
                <ProtectedRoute>
                  <CSTicketDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="my-tickets"
              element={
                <ProtectedRoute>
                  <CSMyTickets />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute requirePermission="cs.admin.manage">
                  <CSAdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute requirePermission="cs.reports.view">
                  <CSReports />
                </ProtectedRoute>
              }
            />
          </Route>
          {/* V17.516 (Phase 0, brief 2026-10-25-003 step 8): a real 403 page, and a real
              404 for every unknown URL inside the app shell (before, an unknown URL
              rendered an empty layout). */}
          <Route path="403" element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />

          {/* V15.0 Stage 0 Scouting routes. V17.523 (Phase 1): moved INSIDE the app shell — the one sidebar now shows on Scout pages too (each route keeps its own guard). */}
          <Route path="scout">
            {/* Scout Performance Dashboard */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute requirePermission="scout.lead.create">
                  <ScoutDashboard />
                </ProtectedRoute>
              }
            />

            {/* V15.1 Scout Command Center - Performance Metrics */}
            <Route
              path="performance"
              element={
                <ProtectedRoute requirePermission="scout.activity.view">
                  <ScoutPerformance />
                </ProtectedRoute>
              }
            />

            {/* V15.1 Scout Command Center - Team Journey History */}
            <Route
              path="journey-history"
              element={
                <ProtectedRoute requirePermission="scout.lead.allocate">
                  <JourneyHistory />
                </ProtectedRoute>
              }
            />

            {/* Scout Mobile Capture Form */}
            <Route
              path="capture"
              element={
                <ProtectedRoute requirePermission="scout.lead.create">
                  <ScoutCapture />
                </ProtectedRoute>
              }
            />

            {/* V17.5: Scout Lead Edit Page */}
            <Route
              path="edit/:leadId"
              element={
                <ProtectedRoute requirePermission="scout.lead.create">
                  <ScoutLeadEdit />
                </ProtectedRoute>
              }
            />

            {/* Manager Lead Allocation Dashboard */}
            <Route
              path="allocation"
              element={
                <ProtectedRoute requirePermission="scout.lead.allocate">
                  <LeadAllocation />
                </ProtectedRoute>
              }
            />

          </Route>

          {/* V17.54: Online Training Module Routes. V17.523 (Phase 1): inside the app shell, as Scout. */}
          <Route path="training">
            <Route
              path="my-courses"
              element={
                <ProtectedRoute requirePermission="training.view">
                  <MyTrainingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="courses/:assignmentId"
              element={
                <ProtectedRoute requirePermission="training.view">
                  <CourseViewerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="manage"
              element={
                <ProtectedRoute requireAnyPermission={['training.create', 'training.manage']}>
                  <CourseManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="manage/:courseId"
              element={
                <ProtectedRoute requireAnyPermission={['training.create', 'training.manage']}>
                  <CourseEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="assignments"
              element={
                <ProtectedRoute requireAnyPermission={['training.manage', 'training.assign']}>
                  <AssignmentManagementPage />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        </Routes>
        </Suspense>
        </ChunkErrorBoundary>
        </ThemeProvider>
        </LanguageProvider>
      </LayoutProvider>
    </AuthProvider>
  );
}

export default App;
